import {
  refineStyleProfileLocally,
  styleRefinementInstructionSchema,
} from '../domain/style/refinement';
import { styleProfileSchema, type StyleProfile } from '../domain/style/schema';

export interface StyleRefinementResult {
  profile: StyleProfile;
  provider: string;
  model: string;
  generatedAt: string;
  warnings: string[];
}

export interface StyleRefiner {
  refine(profile: StyleProfile, instruction: string): Promise<StyleRefinementResult>;
}

export class MockStyleRefiner implements StyleRefiner {
  async refine(profile: StyleProfile, instruction: string): Promise<StyleRefinementResult> {
    const cleanInstruction = styleRefinementInstructionSchema.parse(instruction);
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    return {
      profile: refineStyleProfileLocally(profile, cleanInstruction),
      provider: 'mock',
      model: 'deterministic-written-refinement-v1',
      generatedAt: new Date().toISOString(),
      warnings: ['Local mock mode used a deterministic written refinement.'],
    };
  }
}

export class RemoteStyleRefiner implements StyleRefiner {
  async refine(profile: StyleProfile, instruction: string): Promise<StyleRefinementResult> {
    const cleanInstruction = styleRefinementInstructionSchema.parse(instruction);
    const response = await fetch('/api/style/refine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile, instruction: cleanInstruction }),
    });
    const payload = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      throw new Error(String(payload.message ?? 'Style Card refinement is unavailable.'));
    }
    return {
      profile: styleProfileSchema.parse(payload.profile),
      provider: String(payload.provider),
      model: String(payload.model),
      generatedAt: String(payload.generatedAt),
      warnings: Array.isArray(payload.warnings) ? payload.warnings.map(String) : [],
    };
  }
}

export const getStyleRefiner = (): StyleRefiner => {
  const configured = import.meta.env.VITE_AI_PROVIDER;
  const useCloudflare =
    configured === 'cloudflare' || (import.meta.env.PROD && configured !== 'mock');
  return useCloudflare ? new RemoteStyleRefiner() : new MockStyleRefiner();
};
